import { Alert, Button, Skeleton, Stack, Typography } from '@mui/material'
import { OverlayState } from '@/hooks/useOverlay'
import { useGetApprovalPolicy } from '@/service-library/hooks/approval-policies'
import { ApprovalStatePolicy } from '@/types/approvals'
import { AuthUser } from '@/types/users'
import { ApproverInfo, statePolicyApproverRows } from '@/utils/approver-entries'
import { orderChipLabel } from '@/utils/policy-labels'
import ApprovalPolicyInfo, {
  PolicyConditions,
} from '@/components/approvals/ApprovalPolicyInfo'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'

type ApprovalPolicyDetailsDialogProps = {
  overlay: OverlayState
  policy: ApprovalStatePolicy
  /**
   * Rows to show instead of the ones derived from `policy`. Pass this when
   * the caller only has the live policy config, not a document's progress —
   * those rows say who the policy would ask, not what anyone has decided.
   */
  approvers?: ApproverInfo[]
  /** Names for the positions a policy's approver list holds, by position id. */
  positionNamesById?: Map<string, string>
  positionsPending?: boolean
  /** Approvers by pd user id — see `useUsersById`. */
  usersById: Map<string, AuthUser>
  usersPending: boolean
}

type TriggerConditionProps = {
  policyId: string
}

/**
 * The live trigger condition, which comes from pixydocs-api rather than the
 * approval-state payload — so a deleted policy, or one in a project the reader
 * can't see, has none to show.
 */
function TriggerCondition({ policyId }: TriggerConditionProps) {
  const { approvalPolicy, isPending, isError } = useGetApprovalPolicy({
    id: policyId,
  })

  if (isPending) {
    return (
      <Typography variant="body2">
        <Skeleton width="80%" />
        <Skeleton width="55%" />
      </Typography>
    )
  }

  return (
    <PolicyConditions
      text={isError ? undefined : approvalPolicy?.llm_trigger_prompt}
    />
  )
}

/**
 * Everything behind one approval policy on a document: its order rules, the
 * condition that made it apply, and every approver's decision.
 */
export default function ApprovalPolicyDetailsDialog({
  overlay,
  policy,
  approvers,
  positionNamesById,
  positionsPending,
  usersById,
  usersPending,
}: ApprovalPolicyDetailsDialogProps) {
  return (
    <Dialog
      title={policy.description}
      subtitle="Approval policy"
      maxWidth="sm"
      {...overlay}
    >
      <DialogContent>
        <Stack spacing={2}>
          {!policy.config_resolved && (
            <Alert severity="warning">
              This policy&apos;s live configuration couldn&apos;t be read, so
              its order rules and progress can&apos;t be reported. The decisions
              below are still accurate.
            </Alert>
          )}

          <ApprovalPolicyInfo
            conditions={
              <TriggerCondition policyId={policy.approval_policy_id} />
            }
            orderLabel={orderChipLabel(policy)}
            approvers={approvers ?? statePolicyApproverRows(policy)}
            usersById={usersById}
            positionNamesById={positionNamesById}
            positionsPending={positionsPending}
            usersPending={usersPending}
          />
        </Stack>
      </DialogContent>
      <DialogFooter>
        <Button variant="text" onClick={overlay.close}>
          Close
        </Button>
      </DialogFooter>
    </Dialog>
  )
}
